/**
 * Main App Component
 *
 * Sets up routing, React Query, and toast notifications
 */

import React from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { GoogleOAuthProvider } from '@react-oauth/google';
import { Toaster } from 'react-hot-toast';
import { AuthProvider } from './contexts/AuthContext';
import { SSEProvider } from './contexts/SSEContext';
import PrivateRoute from './components/auth/PrivateRoute';
import LoginPage from './pages/LoginPage';
import { TopNav } from './components/ui/TopNav';
import { ProjectListPage } from './pages/ProjectListPage';
import { ProjectCreatePage } from './pages/ProjectCreatePage';
import { ProjectEditPage } from './pages/ProjectEditPage';
import { ProjectDetailPage } from './pages/ProjectDetailPage';
import { AgentDetailPage } from './pages/AgentDetailPage';
import { BatchDetailPage } from './pages/BatchDetailPage';
import { BatchResultDetailPage } from './pages/BatchResultDetailPage';
import { JobListPage } from './pages/JobListPage';
import { JobDetailPage } from './pages/JobDetailPage';
import { SessionDetailPage } from './pages/SessionDetailPage';
import { PlaygroundPage } from './pages/PlaygroundPage';
import { ApiKeysPage } from './pages/ApiKeysPage';
import { EveViewerPage } from './pages/EveViewerPage';
import { CheckpointPage } from './pages/CheckpointPage';
import { ReplayPage } from './pages/ReplayPage';

// Create a client
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      retry: false, // Per spec: NO retry logic
      refetchOnWindowFocus: false,
    },
    mutations: {
      retry: false, // Per spec: NO retry logic
    },
  },
});

const App: React.FC = () => {
  const googleClientId = process.env.REACT_APP_GOOGLE_CLIENT_ID || '';

  return (
    <GoogleOAuthProvider clientId={googleClientId}>
      <QueryClientProvider client={queryClient}>
        <AuthProvider>
          <SSEProvider>
            <BrowserRouter>
              <Routes>
                {/* Public Routes */}
                <Route path="/login" element={<LoginPage />} />

                {/* Protected Routes */}
                <Route
                  path="/"
                  element={
                    <PrivateRoute>
                      <>
                        <TopNav />
                        <Navigate to="/projects" replace />
                      </>
                    </PrivateRoute>
                  }
                />

                {/* Top-Level Playground Route — PlaygroundPage renders its own TopNav with controls */}
                <Route
                  path="/playground"
                  element={
                    <PrivateRoute>
                      <PlaygroundPage />
                    </PrivateRoute>
                  }
                />

                {/* Replay Route — ReplayPage renders its own TopNav */}
                <Route
                  path="/replay"
                  element={
                    <PrivateRoute>
                      <ReplayPage />
                    </PrivateRoute>
                  }
                />

                {/* API Keys Route */}
                <Route
                  path="/api-keys"
                  element={
                    <PrivateRoute>
                      <>
                        <TopNav />
                        <ApiKeysPage />
                      </>
                    </PrivateRoute>
                  }
                />

                {/* EVE Viewer Route */}
                <Route
                  path="/eve"
                  element={
                    <PrivateRoute>
                      <>
                        <TopNav />
                        <EveViewerPage />
                      </>
                    </PrivateRoute>
                  }
                />

                {/* Project Routes */}
                <Route
                  path="/projects"
                  element={
                    <PrivateRoute>
                      <>
                        <TopNav />
                        <ProjectListPage />
                      </>
                    </PrivateRoute>
                  }
                />
                <Route
                  path="/projects/new"
                  element={
                    <PrivateRoute>
                      <>
                        <TopNav />
                        <ProjectCreatePage />
                      </>
                    </PrivateRoute>
                  }
                />
                <Route
                  path="/projects/:id"
                  element={
                    <PrivateRoute>
                      <>
                        <TopNav />
                        <ProjectEditPage />
                      </>
                    </PrivateRoute>
                  }
                />
                <Route
                  path="/projects/:id/detail"
                  element={
                    <PrivateRoute>
                      <>
                        <TopNav />
                        <ProjectDetailPage />
                      </>
                    </PrivateRoute>
                  }
                />

                {/* Agent Routes */}
                <Route
                  path="/agents/:agent_id"
                  element={
                    <PrivateRoute>
                      <>
                        <TopNav />
                        <AgentDetailPage />
                      </>
                    </PrivateRoute>
                  }
                />

                {/* Batch Routes */}
                <Route
                  path="/batches/:batch_id"
                  element={
                    <PrivateRoute>
                      <>
                        <TopNav />
                        <BatchDetailPage />
                      </>
                    </PrivateRoute>
                  }
                />
                <Route
                  path="/batches/:batch_id/results"
                  element={
                    <PrivateRoute>
                      <>
                        <TopNav />
                        <BatchResultDetailPage />
                      </>
                    </PrivateRoute>
                  }
                />
                <Route
                  path="/batches/:batch_id/jobs"
                  element={
                    <PrivateRoute>
                      <>
                        <TopNav />
                        <JobListPage />
                      </>
                    </PrivateRoute>
                  }
                />

                {/* Job Routes */}
                <Route
                  path="/jobs/:job_id"
                  element={
                    <PrivateRoute>
                      <>
                        <TopNav />
                        <JobDetailPage />
                      </>
                    </PrivateRoute>
                  }
                />

                {/* Session Routes */}
                <Route
                  path="/sessions/:session_id"
                  element={
                    <PrivateRoute>
                      <>
                        <TopNav />
                        <SessionDetailPage />
                      </>
                    </PrivateRoute>
                  }
                />

                {/* Checkpoint Route — interactive checkpoint restore */}
                <Route
                  path="/checkpoint/:session_id"
                  element={
                    <PrivateRoute>
                      <CheckpointPage />
                    </PrivateRoute>
                  }
                />

              </Routes>
            </BrowserRouter>

            {/* Toast Notifications */}
            <Toaster
              position="top-right"
              toastOptions={{
                duration: 4000,
                style: {
                  background: '#fff',
                  color: '#363636',
                },
                success: {
                  duration: 3000,
                  iconTheme: {
                    primary: '#10b981',
                    secondary: '#fff',
                  },
                },
                error: {
                  duration: 5000,
                  iconTheme: {
                    primary: '#ef4444',
                    secondary: '#fff',
                  },
                },
              }}
            />
          </SSEProvider>
        </AuthProvider>
      </QueryClientProvider>
    </GoogleOAuthProvider>
  );
};

export default App;
